import React, { useEffect, useState } from "react";
import styles from "./PricingDashboard.module.scss";
import { 
  Card, 
  Row, 
  Col, 
  Statistic, 
  Space,
  Spin,
  Button,
  Select
} from "antd";
import { 
  DollarOutlined,
  ShoppingOutlined,
  RiseOutlined,
  FallOutlined,
  AlertOutlined,
  BarChartOutlined,
  PieChartOutlined
} from "@ant-design/icons";
import { Bar, Line } from "react-chartjs-2";
import { useTranslation } from "react-i18next";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Title,
  Legend,
  Tooltip,
} from "chart.js";
import { getAllBranchOffices } from "../../../api/Services";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Title,
  Legend,
  Tooltip
);

interface BranchOffice {
  id: number;
  name: string;
}

const PricingDashboard = () => {
  const { t } = useTranslation();
  const [selectedBranchOffice, setSelectedBranchOffice] = useState<number | null>(null);
  const [branchOffices, setBranchOffices] = useState<BranchOffice[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isLoadingBranchOffices, setIsLoadingBranchOffices] = useState<boolean>(true);

  useEffect(() => {
    const fetchBranchOffices = async () => {
      setIsLoadingBranchOffices(true);
      try {
        const offices = await getAllBranchOffices();
        setBranchOffices(offices);
        if (offices.length > 0) {
          setSelectedBranchOffice(offices[0].id);
        }
      } catch (error) {
        console.error("Error fetching branch offices:", error);
      } finally {
        setIsLoadingBranchOffices(false);
      }
    };
    fetchBranchOffices();
  }, []);

  const handleUpdate = () => {
    setIsLoading(true);
    setTimeout(() => {
      setIsLoading(false);
    }, 1000);
  };

  const formatCurrency = (value: number) => {
    return `$${value.toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
  };

  const formatNumber = (value: number) => {
    return value.toLocaleString();
  };

  const pricingAlerts = [
    {
      id: 1,
      type: "price_change",
      title: "Cambio de precio próximo en 5 productos",
      severity: "warning"
    },
    {
      id: 2,
      type: "price_change_recent",
      title: "Cambio de precio reciente en categoría Deportivos",
      severity: "info"
    },
    {
      id: 3,
      type: "promotion_ending",
      title: "Promoción de precios finalizando en 2 días",
      severity: "warning"
    },
    {
      id: 4,
      type: "low_margin",
      title: "Productos con margen bajo detectados",
      severity: "error"
    },
    {
      id: 5,
      type: "price_increase",
      title: "Aumento de precio programado para próxima semana",
      severity: "info"
    }
  ];

  const ventasTotales = {
    valor: 1250000,
    variacion: 12.5,
    esPositiva: true
  };

  const kpis = {
    margenBruto: 35.5,
    ticketPromedio: 1250,
    unidadesPorTransaccion: 3.2
  };

  const tendenciaIngresosData = {
    labels: ["Sem 1", "Sem 2", "Sem 3", "Sem 4", "Sem 5", "Sem 6", "Sem 7", "Sem 8", "Sem 9", "Sem 10", "Sem 11", "Sem 12"],
    datasets: [
      {
        label: "Ingresos",
        data: [950000, 980000, 1020000, 1100000, 1080000, 1150000, 1200000, 1180000, 1220000, 1250000, 1230000, 1250000],
        borderColor: "#1890ff",
        backgroundColor: "rgba(24, 144, 255, 0.1)",
        tension: 0.4,
        fill: true,
        pointRadius: 4,
        pointHoverRadius: 6,
      },
    ],
  };

  const topProductosRentablesData = {
    labels: ["Producto A", "Producto B", "Producto C", "Producto D", "Producto E", "Producto F", "Producto G", "Producto H", "Producto I", "Producto J"],
    datasets: [
      {
        label: "Ganancia",
        data: [45000, 38000, 32000, 28000, 25000, 22000, 20000, 18000, 16000, 15000],
        backgroundColor: "#52c41a",
        borderRadius: 4,
      },
    ],
  };

  const distribucionVentasCategoriaData = {
    labels: [
      "Deportivos",
      "Casuales",
      "Formales",
      "Accesorios",
      "Calzado",
      "Ropa",
      "Electrónica",
      "Hogar",
      "Belleza",
      "Otros"
    ],
    datasets: [
      {
        label: "% de venta",
        data: [25, 18, 15, 12, 10, 8, 5, 4, 2, 1],
        backgroundColor: [
          "#8b5cf6",
          "#1890ff",
          "#f5222d",
          "#fa8c16",
          "#52c41a",
          "#13c2c2",
          "#722ed1",
          "#eb2f96",
          "#faad14",
          "#a0d911"
        ],
        borderRadius: 4,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { 
        display: false 
      },
    },
    scales: {
      y: { 
        beginAtZero: true,
        grid: {
          color: "rgba(0,0,0,0.05)"
        }
      },
      x: {
        grid: {
          display: false
        }
      }
    },
  };

  const lineChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { 
        display: false 
      },
    },
    scales: {
      y: { 
        beginAtZero: true,
        grid: {
          color: "rgba(0,0,0,0.05)"
        },
        ticks: {
          callback: function(value: any) {
            return '$' + (value / 1000) + 'k';
          }
        }
      },
      x: {
        grid: {
          display: false
        }
      }
    },
  };

  const horizontalBarOptions = {
    indexAxis: 'y' as const,
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { 
        display: false 
      },
    },
    scales: {
      x: { 
        beginAtZero: true,
        grid: {
          color: "rgba(0,0,0,0.05)"
        },
        ticks: {
          callback: function(value: any) {
            return value + '%';
          }
        }
      },
      y: {
        grid: {
          display: false
        }
      }
    },
  };

  return (
    <div className={styles.dashboardWrapper}>
      <div className={styles.dashboardHeader}>
        <div className={styles.headerContent}>
          <Space size="middle">
            <Select
              className={styles.branchSelector}
              value={selectedBranchOffice ?? undefined}
              onChange={setSelectedBranchOffice}
              loading={isLoadingBranchOffices}
              placeholder="Seleccionar sucursal"
              style={{ minWidth: 200 }}
            >
              {branchOffices.map((office) => (
                <Select.Option key={office.id} value={office.id}>
                  {office.name}
                </Select.Option>
              ))}
            </Select>
            <Button type="primary" onClick={handleUpdate} className={styles.updateButton}>
              Actualizar
            </Button>
          </Space>
        </div>
      </div>

      <Row gutter={[16, 16]} align="stretch">
        <Col xs={24} lg={18}>
          <div className={styles.kpisSection}>
            <Row gutter={[16, 16]} align="stretch">
              <Col xs={24} sm={12} lg={8}>
                <Card className={styles.kpiCard}>
                  <Spin spinning={isLoading}>
                    <Statistic
                      title="Ventas Totales"
                      value={formatCurrency(ventasTotales.valor)}
                      prefix={<DollarOutlined className={styles.kpiIcon} />}
                      valueStyle={{ color: '#1890ff' }}
                      suffix={
                        <span style={{ 
                          color: ventasTotales.esPositiva ? '#52c41a' : '#f5222d',
                          fontSize: '12px',
                          marginLeft: '8px'
                        }}>
                          {ventasTotales.esPositiva ? <RiseOutlined /> : <FallOutlined />} {Math.abs(ventasTotales.variacion)}%
                        </span>
                      }
                    />
                  </Spin>
                </Card>
              </Col>
              <Col xs={24} sm={12} lg={8}>
                <Card className={styles.kpiCard}>
                  <Spin spinning={isLoading}>
                    <Statistic
                      title="Margen Bruto"
                      value={formatNumber(kpis.margenBruto)}
                      prefix={<BarChartOutlined className={styles.kpiIcon} />}
                      valueStyle={{ color: '#52c41a' }}
                      suffix="%"
                    />
                  </Spin>
                </Card>
              </Col>
              <Col xs={24} sm={12} lg={8}>
                <Card className={styles.kpiCard}>
                  <Spin spinning={isLoading}>
                    <Statistic
                      title="Ticket Promedio"
                      value={formatCurrency(kpis.ticketPromedio)}
                      prefix={<DollarOutlined className={styles.kpiIcon} />}
                      valueStyle={{ color: '#722ed1' }}
                    />
                  </Spin>
                </Card>
              </Col>
            </Row>
            <Row gutter={[16, 16]} align="stretch" style={{ marginTop: 16 }}>
              <Col xs={24} sm={12} lg={8}>
                <Card className={styles.kpiCard}>
                  <Spin spinning={isLoading}>
                    <Statistic
                      title="Unidades por transacción"
                      value={formatNumber(kpis.unidadesPorTransaccion)}
                      prefix={<ShoppingOutlined className={styles.kpiIcon} />}
                      valueStyle={{ color: '#fa8c16' }}
                    />
                  </Spin>
                </Card>
              </Col>
            </Row>
          </div>

          <Row gutter={[16, 16]} className={styles.chartsSection}>
            <Col xs={24} lg={12}>
              <Card 
                title={
                  <Space>
                    <BarChartOutlined />
                    <span>Tendencia de ingresos</span>
                  </Space>
                }
                className={styles.chartCard}
              >
                <div className={styles.chartContainer}>
                  <Line data={tendenciaIngresosData} options={lineChartOptions} />
                </div>
              </Card>
            </Col>
            <Col xs={24} lg={12}>
              <Card 
                title={
                  <Space>
                    <BarChartOutlined />
                    <span>Top productos más rentables</span>
                  </Space>
                }
                className={styles.chartCard}
              >
                <div className={styles.chartContainer}>
                  <Bar data={topProductosRentablesData} options={chartOptions} />
                </div>
              </Card>
            </Col>
          </Row>

          <Row gutter={[16, 16]} className={styles.chartsSection}>
            <Col xs={24}>
              <Card 
                title={
                  <Space>
                    <BarChartOutlined />
                    <span>Distribución de ventas por categoría</span>
                  </Space>
                }
                className={styles.chartCard}
              >
                <div className={styles.chartContainer}>
                  <Bar data={distribucionVentasCategoriaData} options={horizontalBarOptions} />
                </div>
              </Card>
            </Col>
          </Row>
        </Col>

        <Col xs={24} lg={6}>
          <Card 
            title={
              <Space>
                <AlertOutlined />
                <span>Alertas de precios</span>
              </Space>
            }
            className={styles.alertsCard}
          >
            <div className={styles.alertsList}>
              {pricingAlerts.map((alert) => (
                <div key={alert.id} className={styles.alertItem}>
                  <span className={styles.alertArrow}>→</span>
                  <span className={styles.alertText}>{alert.title}</span>
                </div>
              ))}
            </div>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default PricingDashboard;